import { IValidator } from "../../../Validation/IValidator";
import { AddCreditCardModel } from "@truckdown/billing";

interface IPropertyValidator {
    key: string;
    validator: () => boolean;
    message: () => string;
}

interface ValidationObject {
    msg: string;
    isValid: boolean;
}

export class CreditCardValidator implements IValidator {
    constructor(ccard: AddCreditCardModel) {
        this._ccard = ccard;
        this._validators = this.getValidators();
    }
    private _ccard: AddCreditCardModel;
    private _validators: IPropertyValidator[];
    private getValidators(): IPropertyValidator[] {

        return [
            {
                key: "name",
                message: () => this.validateCreditCardNameMessage(this._ccard.name),
                validator: () => this.validateCreditCardName(this._ccard.name)
            },
            {
                key: "number",
                message: () => this.validateCreditCardMessage(this._ccard.number.trim()),
                validator: () => this.validateCreditCard(this._ccard.number.trim()),
            },
            {
                key: "numberFinal",
                message: () => "Invalid card number.",
                validator: () => this.luhnCheck(this._ccard.number.trim()),
            },
            {
                key: "codeFinal",
                message: () => "Invalid security code format. Please check the 3-digit security code on the back of your credit card and try again.",
                validator: () => /^[0-9]{3,4}$/.test(this._ccard.securityCode.trim()) && this._ccard.securityCode.trim().length > 0
            },
            {
                key: "securityCode",
                message: () => "Security code required.",
                validator: () => this._ccard.securityCode.length > 0
            },
            {
                key: "expiryMonth",
                message: () => "Expiry month required.",
                validator: () => this._ccard.expiryMonth > 0
            },
            {
                key: "expiryYear",
                message: () => "Expiration year is required.",
                validator: () => this._ccard.expiryYear > 0
            },
            {
                key: "expiry",
                message: () => "Credit card has already expired.",
                validator: () => this.validateExpiry(this._ccard.expiryMonth, this._ccard.expiryYear)
            }
        ];
    };

    // name validation
    private validateCreditCardNameMessage(name: string): string {
        if (!name || name.trim().length < 1) {
            return "Name is required.";
        }
        if (!/^[a-zA-Z ]+$/.test(name)) {
            return "Invalid characters in name.";
        }
        if (name.trim().length > 50) {
            return "Name is too long.";
        }
        return "";
    }

    private validateCreditCardName(name: string): boolean {
        let isValid = false;
        if (name && name.trim().length > 0 && /^[a-zA-Z ]+$/.test(name) && (name.trim().length > 1 && name.trim().length < 51)) {
            isValid = true;
        }

        return isValid;
    }

    //card number validation
    private validateCreditCardMessage(number: string): string {
        if (!number || number.trim().length < 1) {
            return "Credit card number is required.";
        }
        if (!/^[0-9\s]+$/.test(number)) {
            return "Credit card number should contain only digits.";
        }
        if (number.length > 19) {
            return "Credit card number is too long. Please enter a valid number.";
        }
        return "";
    }

    private validateCreditCard(number: string): boolean {
        let isValid = false;
        if (number && number.length > 0 && /^[0-9\s]+$/.test(number) && this.luhnCheck(number) && number.length < 20) {
            isValid = true;
        }

        return isValid;
    }

    private luhnCheck(cardNumber: string): boolean {
        let sum = 0;
        let numDigits = cardNumber.length;
        let parity = numDigits % 2;
        for (let i = 0; i < numDigits; i++) {
            let digit = parseInt(cardNumber.charAt(i));
            if (i % 2 === parity) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }
            sum += digit;
        }
        return sum % 10 === 0;
    }

    private validateExpiry(expirationMonth: number, expirationYear: number): boolean {
        const currentDate = new Date();
        const cardExpirationDate = new Date(expirationYear, expirationMonth - 1, 1);
        return cardExpirationDate > currentDate;
    }

    validate(key?: string): boolean {
        if (key) {
            let validator = this._validators.find(v => v.key == key);
            if (validator) {
                const isValid = validator.validator();
                if (!isValid) {
                    this.errors[key] = validator.message();
                } else {
                    delete this.errors[key];
                }
                return isValid;
            }
            return true;
        } else {
            let isValid = true;
            for (let i = 0; i < this._validators.length; i++) {
                let v = this._validators[i];
                if (!v.validator()) {
                    isValid = false;
                    delete this.errors[v.key];
                    this.errors[v.key] = v.message();
                } else {
                    delete this.errors[v.key];
                }
            }
            return isValid;
        }
    }

    reset() {
        this.errors = {};
    }

    errors: { [key: string]: string } = {};
}